/**
 * Voicemail box detail — list of messages for one box. Mirrors FolderDetail's
 * "list → detail" flow: /voicemails shows boxes; clicking a row brings you
 * here at /voicemails/:id. Replaces the old sidebar-grid layout where boxes
 * + messages shared one page.
 */
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { voicemails } from '@/api/voicemails';
import type { VoicemailRow } from '@/api/voicemails';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft, Voicemail, Mail, MailOpen, Trash2, Play, Square, Download,
  CheckCircle2, XCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/cn';

const MUTATE_ROLES = ['super_admin', 'reseller_owner', 'office_manager'];

// Page-size options — matches the folder popup + folder detail page.
const VM_PAGE_SIZES = [50, 100, 200, 'ALL'] as const;
type VmPageSize = typeof VM_PAGE_SIZES[number];

function durationLabel(ms: number | null): string {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}

export function VoicemailBoxDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: box, isLoading, error } = useQuery({
    queryKey: ['voicemailBox', id],
    queryFn: () => voicemails.getBox(id!),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return <div className="text-sm text-slate-400">{t('common.loading')}</div>;
  }

  if (error || !box) {
    return (
      <div className="text-sm text-red-500">
        Voicemail box not found.{' '}
        <Link to="/voicemails" className="underline">Back to voicemails</Link>
      </div>
    );
  }

  return <VoicemailBoxDetailInner box={box} />;
}

function VoicemailBoxDetailInner({
  box,
}: {
  box: import('@/api/voicemails').VoicemailBoxRow;
}) {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canMutate = MUTATE_ROLES.includes(user.role);
  const qc = useQueryClient();
  const accessToken = useAuth((s) => s.accessToken);

  const [playingId, setPlayingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['voicemails', box.id],
    queryFn: () => voicemails.listMessages(box.id, { limit: 500 }),
  });
  const rows = data?.rows ?? [];

  // ── Pagination (client-side) ──────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<VmPageSize>(50);
  const pageCount = pageSize === 'ALL'
    ? 1
    : Math.max(1, Math.ceil(rows.length / pageSize));
  const startIdx = pageSize === 'ALL' ? 0 : page * pageSize;
  const endIdx = pageSize === 'ALL' ? rows.length : Math.min(startIdx + pageSize, rows.length);
  const visibleRows = rows.slice(startIdx, endIdx);
  const showingFrom = rows.length === 0 ? 0 : startIdx + 1;
  const showingTo = endIdx;
  // Clamp page if rows shrink (e.g. after a bulk delete on the last page).
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const invalidateMessages = (): void => {
    qc.invalidateQueries({ queryKey: ['voicemails', box.id] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => voicemails.markRead(id),
    onSuccess: invalidateMessages,
  });
  const unmarkRead = useMutation({
    mutationFn: (id: string) => voicemails.markUnread(id),
    onSuccess: invalidateMessages,
  });
  const removeMsg = useMutation({
    mutationFn: (id: string) => voicemails.removeMessage(id),
    onSuccess: (_data, id) => {
      invalidateMessages();
      setPlayingId((prev) => (prev === id ? null : prev));
    },
  });

  // ── Bulk operations ────────────────────────────────────────────────────
  const bulkRead = useMutation({
    mutationFn: () => voicemails.bulkMarkRead(box.id, Array.from(selected)),
    onSuccess: () => { setSelected(new Set()); invalidateMessages(); },
  });
  const bulkUnread = useMutation({
    mutationFn: () => voicemails.bulkMarkUnread(box.id, Array.from(selected)),
    onSuccess: () => { setSelected(new Set()); invalidateMessages(); },
  });
  const bulkDel = useMutation({
    mutationFn: () => voicemails.bulkDelete(box.id, Array.from(selected)),
    onSuccess: () => { setSelected(new Set()); setPlayingId(null); invalidateMessages(); },
  });

  // Selection helpers — operate on the visible page.
  const allSelectedOnPage = visibleRows.length > 0 && visibleRows.every((m) => selected.has(m.id));
  const someSelectedOnPage = visibleRows.some((m) => selected.has(m.id));
  function toggleAllOnPage(checked: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) visibleRows.forEach((m) => next.add(m.id));
      else visibleRows.forEach((m) => next.delete(m.id));
      return next;
    });
  }
  function toggleOne(id: string, checked: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }

  /**
   * Voicemail rows may legitimately exist without an audio file — gate
   * Play/Download on s3_key being present so users see "no audio" rather
   * than a broken player.
   */
  function hasAudio(r: VoicemailRow): boolean {
    return typeof r.s3_key === 'string' && r.s3_key.length > 0;
  }

  return (
    <div className="space-y-5">
      {/* Back link + header — same shape as FolderDetail */}
      <div>
        <Link
          to="/voicemails"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All voicemail boxes
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <Voicemail className="h-5 w-5 text-amber-600 shrink-0" />
          <h1 className="text-lg font-semibold">{box.name}</h1>
          <span className="text-[10px] uppercase tracking-wider rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-amber-700">
            {box.owner_kind.replace('_', ' ')}
          </span>
          <span className="text-xs text-muted-foreground">
            · {rows.length} message{rows.length === 1 ? '' : 's'}
          </span>
        </div>

        <CustomerScopeChip />
      </div>

      <section className="min-w-0 space-y-3">
        {/* Bulk action bar — visible whenever there are messages so the
            select-all checkbox is always reachable. The action buttons
            appear once the user has at least one message selected. */}
        {!isLoading && rows.length > 0 && canMutate && (
          <div className="flex items-center gap-2 flex-wrap">
            <Checkbox
              checked={allSelectedOnPage}
              data-state={!allSelectedOnPage && someSelectedOnPage ? 'indeterminate' : undefined}
              onCheckedChange={(v) => toggleAllOnPage(Boolean(v))}
              aria-label="Select all messages on this page"
            />
            <span className="text-xs text-muted-foreground">
              {selected.size > 0 ? `${selected.size} selected` : 'Select all on page'}
            </span>
            {selected.size > 0 && (
              <>
                {/* Bulk Mark read / Mark unread — icon-only buttons. Per-row
                    buttons use the same icons; aria-label + title carry the
                    verb for tooltips and screen readers. */}
                <Button
                  size="sm" variant="outline"
                  onClick={() => bulkRead.mutate()}
                  disabled={bulkRead.isPending}
                  className="h-7 w-7 p-0"
                  aria-label="Mark selected as read"
                  title="Mark selected as read"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => bulkUnread.mutate()}
                  disabled={bulkUnread.isPending}
                  className="h-7 w-7 p-0"
                  aria-label="Mark selected as unread"
                  title="Mark selected as unread"
                >
                  <XCircle className="h-3.5 w-3.5 text-amber-600" />
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    voicemails.downloadZip(box.id, Array.from(selected))
                      .catch(() => undefined);
                  }}
                  className="h-7"
                  title="Download selected messages as a ZIP"
                >
                  <Download className="h-3.5 w-3.5 me-1.5" />
                  Download .zip
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    if (!confirm(`Delete ${selected.size} message${selected.size === 1 ? '' : 's'}?`)) return;
                    bulkDel.mutate();
                  }}
                  disabled={bulkDel.isPending}
                  className="h-7 text-destructive border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5 me-1.5" />
                  Delete
                </Button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                >
                  Clear
                </button>
              </>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-slate-400">{t('voicemail.loadingMessages')}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
            {t('voicemail.noMessagesYet')}
          </div>
        ) : (
          <>
            <div className="rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canMutate && <TableHead className="w-8" />}
                    {/* Leftmost state-envelope column removed — the read/unread
                        envelope now lives in the actions column on the right
                        as a single combined indicator + toggle. */}
                    <TableHead>{t('voicemail.columnFrom')}</TableHead>
                    <TableHead>{t('voicemail.columnDuration')}</TableHead>
                    <TableHead>{t('voicemail.columnReceived')}</TableHead>
                    <TableHead className="w-44 text-end">{t('voicemail.columnActions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((r) => {
                    const isPlaying = playingId === r.id;
                    const audioOk = hasAudio(r);
                    return (
                      <Fragment key={r.id}>
                        <TableRow
                          className={cn('transition-colors', isPlaying && 'bg-blue-50/60')}
                        >
                          {canMutate && (
                            <TableCell className="pe-0">
                              <Checkbox
                                checked={selected.has(r.id)}
                                onCheckedChange={(v) => toggleOne(r.id, Boolean(v))}
                                aria-label="Select message"
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-sm">
                            {/* When the row is playing, render the compact
                                player INLINE next to the number — matches
                                the folder-detail pattern (per Sam 2026-05-17).
                                Number truncates to make room. Only the
                                clicked row mounts a player, so we're not
                                loading audio bytes for every voicemail in
                                the list. */}
                            {isPlaying && audioOk ? (
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="truncate max-w-[7rem] text-slate-600">
                                  {r.from_number ?? '—'}
                                </span>
                                <div className="w-72 shrink-0">
                                  <AudioPlayer
                                    src={voicemails.audioUrl(r.id, accessToken)}
                                    compact
                                  />
                                </div>
                              </div>
                            ) : (
                              <span>{r.from_number ?? '—'}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {durationLabel(r.duration_ms)}
                          </TableCell>
                          <TableCell
                            className="text-xs text-muted-foreground"
                            title={new Date(r.created_at).toLocaleString()}
                          >
                            {new Date(r.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {audioOk ? (
                                <button
                                  type="button"
                                  aria-label={isPlaying ? 'Stop playback' : 'Play voicemail'}
                                  onClick={() => {
                                    setPlayingId((prev) => (prev === r.id ? null : r.id));
                                    if (!isPlaying && !r.read_at && canMutate) {
                                      markRead.mutate(r.id);
                                    }
                                  }}
                                  className={cn(
                                    'rounded p-1.5 transition-colors',
                                    isPlaying
                                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                                  )}
                                >
                                  {isPlaying ? (
                                    <Square className="h-3.5 w-3.5 fill-current" />
                                  ) : (
                                    <Play className="h-3.5 w-3.5 fill-current" />
                                  )}
                                </button>
                              ) : (
                                <span
                                  className="text-[11px] italic text-slate-400 pe-1"
                                  title={t('voicemail.noAudio')}
                                >
                                  {t('voicemail.noAudio')}
                                </span>
                              )}
                              {audioOk && (
                                <button
                                  type="button"
                                  aria-label="Download message"
                                  title="Download message"
                                  onClick={() => {
                                    voicemails.downloadOne(r.id).catch(() => undefined);
                                  }}
                                  className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {/* Envelope toggle — single icon that BOTH
                                  shows read/unread status AND toggles it
                                  on click. Replaces the separate left
                                  state-cell + right action-button pair.
                                  MailOpen (slate) = currently read,
                                  Mail (amber) = currently unread. */}
                              {canMutate && (
                                <Button
                                  size="sm" variant="ghost"
                                  onClick={() => (r.read_at ? unmarkRead.mutate(r.id) : markRead.mutate(r.id))}
                                  disabled={markRead.isPending || unmarkRead.isPending}
                                  aria-label={r.read_at ? t('voicemail.markUnread') : t('voicemail.markRead')}
                                  title={r.read_at ? t('voicemail.markUnread') : t('voicemail.markRead')}
                                  className="h-8 w-8 p-0"
                                >
                                  {r.read_at ? (
                                    <MailOpen className="h-4 w-4 text-slate-400" />
                                  ) : (
                                    <Mail className="h-4 w-4 text-amber-600" />
                                  )}
                                </Button>
                              )}
                              {canMutate && (
                                <Button
                                  size="sm" variant="ghost"
                                  onClick={() => removeMsg.mutate(r.id)}
                                  disabled={removeMsg.isPending}
                                  aria-label="Delete voicemail"
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {/* Player rendered INLINE in the From cell above
                            now (Sam 2026-05-17) — only mounts for the
                            clicked row, matches FolderDetail pattern.
                            The full-width below-row player has been
                            removed: it doubled every row's height and
                            preloaded audio bytes for every voicemail in
                            the list. */}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination footer */}
            <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
              <div className="text-muted-foreground">
                Showing {showingFrom}–{showingTo} of {rows.length}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPageSize(v === 'ALL' ? 'ALL' : (Number(v) as VmPageSize));
                    setPage(0);
                  }}
                  className="rounded border border-input bg-background px-2 py-1 text-xs"
                >
                  {VM_PAGE_SIZES.map((n) => (
                    <option key={String(n)} value={String(n)}>{n}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0 || pageSize === 'ALL'}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="font-mono">
                  Page {page + 1} of {pageCount}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page + 1 >= pageCount || pageSize === 'ALL'}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
